// finance analytics page
import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  AttachMoney as MoneyIcon,
  Receipt as ReceiptIcon,
  Payment as PaymentIcon,
  Download as DownloadIcon,
  DateRange as DateRangeIcon
} from '@mui/icons-material';

const Analytics = () => {
  const [timeRange, setTimeRange] = useState('30');
  const [selectedMonth, setSelectedMonth] = useState('2024-01');

  // Mock data for finance analytics
  const mockAnalytics = {
    overview: {
      totalRevenue: 125000,
      totalInvoices: 45,
      paidInvoices: 38,
      pendingPayments: 7,
      outstandingAmount: 15000,
      revenueGrowth: 12.5,
      paymentSuccessRate: 84.4
    },
    monthlyData: [
      { month: 'Jan 2024', revenue: 125000, invoices: 45, paid: 38, pending: 7 },
      { month: 'Dec 2023', revenue: 110000, invoices: 42, paid: 35, pending: 7 },
      { month: 'Nov 2023', revenue: 98000, invoices: 38, paid: 32, pending: 6 },
      { month: 'Oct 2023', revenue: 89000, invoices: 35, paid: 30, pending: 5 }
    ],
    topCustomers: [
      { name: 'TechCorp Solutions', revenue: 25000, invoices: 8, status: 'active' },
      { name: 'Global Industries', revenue: 22000, invoices: 6, status: 'active' },
      { name: 'Innovate Corp', revenue: 18000, invoices: 5, status: 'active' },
      { name: 'StartupXYZ', revenue: 12000, invoices: 4, status: 'inactive' }
    ],
    planRevenue: [
      { plan: 'Free', revenue: 0, customers: 15, percentage: 0 },
      { plan: 'Basic', revenue: 75000, customers: 25, percentage: 60 },
      { plan: 'Advance', revenue: 50000, customers: 10, percentage: 40 }
    ],
    paymentMethods: [
      { method: 'Credit Card', count: 25, amount: 75000, percentage: 60 },
      { method: 'Bank Transfer', count: 12, amount: 35000, percentage: 28 },
      { method: 'Digital Wallet', count: 8, amount: 15000, percentage: 12 }
    ]
  };

  const handleTimeRangeChange = (event) => {
    setTimeRange(event.target.value);
  };

  const handleMonthChange = (event) => {
    setSelectedMonth(event.target.value);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const getGrowthColor = (growth) => {
    return growth >= 0 ? 'success' : 'error';
  };

  const getGrowthIcon = (growth) => {
    return growth >= 0 ? <TrendingUpIcon /> : <TrendingDownIcon />;
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" component="h1">
          Finance Analytics
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <FormControl size="small">
            <InputLabel>Time Range</InputLabel>
            <Select
              value={timeRange}
              label="Time Range"
              onChange={handleTimeRangeChange}
            >
              <MenuItem value="7">Last 7 days</MenuItem>
              <MenuItem value="30">Last 30 days</MenuItem>
              <MenuItem value="90">Last 90 days</MenuItem>
              <MenuItem value="365">Last year</MenuItem>
            </Select>
          </FormControl>
          <TextField
            type="month"
            value={selectedMonth}
            onChange={handleMonthChange}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <DateRangeIcon />
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={() => {/* Handle export */}}
          >
            Export
          </Button>
        </Box>
      </Box>

      {/* Overview Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Total Revenue
                  </Typography>
                  <Typography variant="h4" component="div">
                    {formatCurrency(mockAnalytics.overview.totalRevenue)}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                    <Chip
                      icon={getGrowthIcon(mockAnalytics.overview.revenueGrowth)}
                      label={`${mockAnalytics.overview.revenueGrowth}%`}
                      color={getGrowthColor(mockAnalytics.overview.revenueGrowth)}
                      size="small"
                    />
                  </Box>
                </Box>
                <MoneyIcon sx={{ fontSize: 40, color: 'primary.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Total Invoices
                  </Typography>
                  <Typography variant="h4" component="div">
                    {mockAnalytics.overview.totalInvoices}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {mockAnalytics.overview.paidInvoices} paid, {mockAnalytics.overview.pendingPayments} pending
                  </Typography>
                </Box>
                <ReceiptIcon sx={{ fontSize: 40, color: 'secondary.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Outstanding Amount
                  </Typography>
                  <Typography variant="h4" component="div">
                    {formatCurrency(mockAnalytics.overview.outstandingAmount)}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {mockAnalytics.overview.pendingPayments} pending payments
                  </Typography>
                </Box>
                <PaymentIcon sx={{ fontSize: 40, color: 'warning.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Payment Success Rate
                  </Typography>
                  <Typography variant="h4" component="div">
                    {mockAnalytics.overview.paymentSuccessRate}%
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {mockAnalytics.overview.paidInvoices} of {mockAnalytics.overview.totalInvoices} invoices
                  </Typography>
                </Box>
                <TrendingUpIcon sx={{ fontSize: 40, color: 'success.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Detailed Analytics */}
      <Grid container spacing={3}>
        {/* Monthly Revenue Trend */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Monthly Revenue Trend
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Month</TableCell>
                      <TableCell align="right">Revenue</TableCell>
                      <TableCell align="right">Invoices</TableCell>
                      <TableCell align="right">Paid</TableCell>
                      <TableCell align="right">Pending</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {mockAnalytics.monthlyData.map((row) => (
                      <TableRow key={row.month}>
                        <TableCell>{row.month}</TableCell>
                        <TableCell align="right">{formatCurrency(row.revenue)}</TableCell>
                        <TableCell align="right">{row.invoices}</TableCell>
                        <TableCell align="right">{row.paid}</TableCell>
                        <TableCell align="right">{row.pending}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Top Customers */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Top Revenue Customers
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Customer</TableCell>
                      <TableCell align="right">Revenue</TableCell>
                      <TableCell align="right">Invoices</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {mockAnalytics.topCustomers.map((customer) => (
                      <TableRow key={customer.name}>
                        <TableCell>{customer.name}</TableCell>
                        <TableCell align="right">{formatCurrency(customer.revenue)}</TableCell>
                        <TableCell align="right">{customer.invoices}</TableCell>
                        <TableCell>
                          <Chip
                            label={customer.status}
                            color={customer.status === 'active' ? 'success' : 'default'}
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Plan Revenue Distribution */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Revenue by Plan
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Plan</TableCell>
                      <TableCell align="right">Revenue</TableCell>
                      <TableCell align="right">Customers</TableCell>
                      <TableCell align="right">Percentage</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {mockAnalytics.planRevenue.map((plan) => (
                      <TableRow key={plan.plan}>
                        <TableCell>{plan.plan}</TableCell>
                        <TableCell align="right">{formatCurrency(plan.revenue)}</TableCell>
                        <TableCell align="right">{plan.customers}</TableCell>
                        <TableCell align="right">{plan.percentage}%</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Payment Methods */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Payment Methods
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Method</TableCell>
                      <TableCell align="right">Count</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      <TableCell align="right">Percentage</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {mockAnalytics.paymentMethods.map((method) => (
                      <TableRow key={method.method}>
                        <TableCell>{method.method}</TableCell>
                        <TableCell align="right">{method.count}</TableCell>
                        <TableCell align="right">{formatCurrency(method.amount)}</TableCell>
                        <TableCell align="right">{method.percentage}%</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Analytics;